import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField, Typography } from "@mui/material";
import axios from "axios";
import { useAlert } from "contexts/alert";
import { useState } from "react";
import { Link } from "react-router-dom";
import { errorMessageOf } from "v2/utils/errorMessageOf";

import { MonitorAction } from "./MonitorQueue";
import { closeForPeople, decideByHr, MAX_CLOSE_KEYS, MAX_REASON_LENGTH, resendNow } from "./monitor.service";
import { CampaignMonitorResponse, MonitorableType, QueueRow } from "./monitor.types";
import { canResend, MAX_MANUAL_RESENDS_PER_DAY, pendingPeople, rowKey } from "./monitor.utils";

export interface MonitorActionDialogProps {
  action: MonitorAction;
  data: CampaignMonitorResponse;
  type: MonitorableType;
  onClose: () => void;
  onDone: () => void;
}

type Severity = "success" | "warning" | "error";
/** why: `nothingSaved` mantém o diálogo aberto (com motivo e escolhas) quando a tentativa não gravou nada. */
type Outcome = [message: string, severity: Severity, nothingSaved?: boolean];

const TITLES: Record<MonitorAction["kind"], string> = { RESEND: "Reenviar agora", DECIDE: "Decidir pelo RH", CLOSE: "Encerrar envios" };
const CONFIRM: Record<MonitorAction["kind"], string> = { RESEND: "Enviar agora", DECIDE: "Registrar decisão", CLOSE: "Encerrar envios" };
const FALLBACK: Record<MonitorAction["kind"], string> = {
  RESEND: "Não foi possível reenviar o convite.",
  DECIDE: "Não foi possível registrar a decisão.",
  CLOSE: "Não foi possível encerrar os envios.",
};

/** why: os `Error` de validação do serviço não têm `response`, e `errorMessageOf` só lê a resposta do servidor. */
const messageOf = (e: unknown, fallback: string): string =>
  e instanceof Error && !axios.isAxiosError(e) ? e.message : errorMessageOf(e, fallback);

/**
 * why: 4xx e validação local barram antes de qualquer escrita; 5xx ou rede podem ter gravado parte, porque o
 * save de Exceções grava pessoa a pessoa, sem transação.
 */
const surelyNothingSaved = (e: unknown): boolean => {
  const status = Number((e as { response?: { status?: unknown } } | null)?.response?.status);
  return (status >= 400 && status < 500) || (e instanceof Error && !axios.isAxiosError(e));
};

const personKey = (r: QueueRow, taxPayerId: string) => `${rowKey(r)}:${taxPayerId}`;

/**
 * invariant: chama `onDone` (recarrega e fecha) sempre que houve resposta ou algo pode ter sido gravado: falha
 * parcial, 5xx ou rede no decidir, `closed: 0` no encerrar e todo reenvio. Só o erro que garante nada gravado
 * (erro no encerrar; 4xx ou validação no decidir sem nenhuma registrada) deixa o diálogo aberto para tentar de novo.
 */
export function MonitorActionDialog({ action, data, type, onClose, onDone }: MonitorActionDialogProps) {
  const { openAlert } = useAlert();
  const [reason, setReason] = useState("");
  const [choices, setChoices] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);

  const { kind, rows } = action;
  const batch = rows.length > 1;
  const single = rows[0];
  const contractOf = (ws: string) => data.contracts.find((c) => c.workspaceId === ws);
  const needsReason = kind !== "RESEND" || batch;
  const resendable = rows.filter(canResend);
  const closeKeys = rows.flatMap((r) => r.openOccurrenceKeys);
  const tooManyKeys = closeKeys.length > MAX_CLOSE_KEYS;
  const ready =
    kind === "RESEND" ? resendable.length > 0 : kind === "CLOSE" ? closeKeys.length > 0 && !tooManyKeys : Object.keys(choices).length > 0;
  const canSubmit = !busy && ready && (!needsReason || reason.trim().length > 0);

  async function resend(): Promise<Outcome> {
    const sent: string[] = [];
    const failed: string[] = [];
    for (const r of resendable) {
      const who = batch ? `${r.name}: ` : "";
      try {
        // invariant: canResend garante a chave.
        const result = await resendNow(r.workspaceId, type, r.primaryOccurrenceKey as string, batch ? reason : undefined);
        if (result.status === "ENVIADO") sent.push(result.to);
        else failed.push(`${who}Não foi possível enviar${result.error ? `: ${result.error}` : "."}`);
      } catch (e) {
        failed.push(`${who}${messageOf(e, FALLBACK.RESEND)}`);
      }
    }
    if (!batch) return sent.length ? [`Convite enviado para ${sent[0]}.`, "success"] : [failed[0], "error"];
    const skipped = rows.length - resendable.length;
    const parts = [sent.length ? `${sent.length} convite(s) enviado(s).` : "Nenhum convite enviado."];
    if (failed.length) parts.push(`Não enviados: ${failed.join("; ")}`.replace(/\.?$/, "."));
    if (skipped) parts.push(`${skipped} fora do envio: sem canal, sem ocorrência aberta ou no limite de hoje.`);
    return [parts.join(" "), failed.length || skipped ? (sent.length ? "warning" : "error") : "success"];
  }

  async function close(): Promise<Outcome> {
    const { closed } = await closeForPeople(data.scope.organizationId, type, closeKeys, reason);
    // why: `closed: 0` é resposta, não erro (R32): a chave já foi fechada por outro caminho; fechar e recarregar mostra a fila verdadeira.
    if (!closed) return ["Nenhuma ocorrência encerrada: já estavam encerradas ou são de outro contrato.", "warning"];
    const rest = closeKeys.length - closed;
    const tail = rest > 0 ? ` ${rest} já estavam encerradas ou são de outro contrato.` : "";
    return [`${closed} ocorrência(s) encerrada(s). A decisão continua pendente.${tail}`, rest > 0 ? "warning" : "success"];
  }

  async function decide(): Promise<Outcome> {
    const byApp = new Map<string, { label: string; decisions: { taxPayerId: string; isOptant: boolean }[] }>();
    for (const r of rows) {
      const contract = contractOf(r.workspaceId);
      if (!contract?.exceptionWorkspaceActivityId) continue;
      for (const p of pendingPeople(r)) {
        const choice = choices[personKey(r, p.taxPayerId)];
        if (choice === undefined) continue;
        const entry = byApp.get(contract.exceptionWorkspaceActivityId) ?? { label: contract.label, decisions: [] };
        entry.decisions.push({ taxPayerId: p.taxPayerId, isOptant: choice });
        byApp.set(contract.exceptionWorkspaceActivityId, entry);
      }
    }
    let done = 0;
    let uncertain = false;
    const failed: string[] = [];
    for (const [appId, { label, decisions }] of byApp) {
      try {
        await decideByHr(appId, decisions, reason);
        done += decisions.length;
      } catch (e) {
        if (surelyNothingSaved(e)) {
          failed.push(`Não registrada em ${label}: ${messageOf(e, FALLBACK.DECIDE)}`);
        } else {
          uncertain = true;
          failed.push(`A decisão pode ter sido registrada em parte em ${label}; confira a fila.`);
        }
      }
    }
    const parts = [...(done ? [`Decisão do RH registrada para ${done} pessoa(s).`] : []), ...failed];
    const nothingSaved = done === 0 && !uncertain;
    return [parts.join(" "), !failed.length ? "success" : nothingSaved ? "error" : "warning", nothingSaved];
  }

  async function submit() {
    setBusy(true);
    let keepOpen = true;
    try {
      const [message, severity, nothingSaved = false] = kind === "RESEND" ? await resend() : kind === "CLOSE" ? await close() : await decide();
      openAlert(message, severity);
      keepOpen = nothingSaved;
    } catch (e) {
      openAlert(messageOf(e, FALLBACK[kind]), "error");
    }
    setBusy(false);
    if (!keepOpen) onDone();
  }

  const editPath = single ? contractOf(single.workspaceId)?.editDataPath : null;

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{batch ? `${TITLES[kind]} (${rows.length} titulares)` : TITLES[kind]}</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {kind === "RESEND" && !batch && (
          <>
            {resendable.length ? (
              <Typography>{`O convite vai para: ${single.email}`}</Typography>
            ) : (
              <Typography>Este titular não pode receber reenvio agora: sem canal, sem ocorrência aberta ou no limite de hoje.</Typography>
            )}
            {/* why: a fila só oferece "Corrigir e-mail" quando falta e-mail; aqui é o e-mail presente que pode estar errado. */}
            {editPath && (
              <Button variant="text" component={Link} to={`${editPath}?cpf=${single.taxPayerId}`} sx={{ alignSelf: "flex-start" }}>
                Corrigir e-mail
              </Button>
            )}
            <Typography variant="body2">{`Mesmo link, fora da cadência; não muda o passo. Até ${MAX_MANUAL_RESENDS_PER_DAY} por titular por dia.`}</Typography>
          </>
        )}
        {kind === "RESEND" && batch && (
          <Typography variant="body2">
            {`${resendable.length} de ${rows.length} podem receber agora, cada um com o próprio link. Quem está sem canal, sem ocorrência aberta ou já recebeu ${MAX_MANUAL_RESENDS_PER_DAY} hoje fica de fora.`}
          </Typography>
        )}
        {kind === "CLOSE" && (
          <>
            <Typography variant="body2">Para os envios. A pessoa continua sem decisão e a inclusão continua em espera.</Typography>
            {!closeKeys.length && <Typography variant="body2">Nenhuma ocorrência aberta para encerrar.</Typography>}
            {tooManyKeys && (
              <Alert severity="warning">
                {`Selecione menos titulares: a seleção soma ${closeKeys.length} ocorrências abertas e o limite é ${MAX_CLOSE_KEYS} por vez.`}
              </Alert>
            )}
          </>
        )}
        {kind === "DECIDE" &&
          rows.map((r) => {
            const contract = contractOf(r.workspaceId);
            return (
              <Box key={rowKey(r)}>
                {batch && <Typography variant="subtitle2">{r.name}</Typography>}
                {!contract?.exceptionWorkspaceActivityId ? (
                  <Typography variant="body2">{`${contract?.label ?? r.workspaceId}: contrato sem app de Exceções; a decisão do RH não pode ser registrada aqui.`}</Typography>
                ) : !pendingPeople(r).length ? (
                  <Typography variant="body2">Nada a decidir para este titular: todos já têm decisão.</Typography>
                ) : (
                  pendingPeople(r).map((p) => {
                    const k = personKey(r, p.taxPayerId);
                    return (
                      <RadioGroup
                        key={k}
                        row
                        value={choices[k] === undefined ? "" : choices[k] ? "SIM" : "NAO"}
                        onChange={(e) => setChoices((cur) => ({ ...cur, [k]: e.target.value === "SIM" }))}
                      >
                        <Typography sx={{ minWidth: 180, alignSelf: "center" }}>{p.name}</Typography>
                        <FormControlLabel value="SIM" control={<Radio inputProps={{ "aria-label": `${p.name}: Sim` }} />} label="Sim" />
                        <FormControlLabel value="NAO" control={<Radio inputProps={{ "aria-label": `${p.name}: Não` }} />} label="Não" />
                      </RadioGroup>
                    );
                  })
                )}
              </Box>
            );
          })}
        {needsReason && (
          <TextField
            label="Motivo"
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            minRows={2}
            inputProps={{ maxLength: MAX_REASON_LENGTH }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Voltar</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>{CONFIRM[kind]}</Button>
      </DialogActions>
    </Dialog>
  );
}
